import type { Plugin } from "../../core/plugin";
import { PluginRegistry } from "../../core/pluginRegistry";

export class Filters implements Plugin {
  private readonly button: HTMLElement | null;
  private readonly panel: HTMLElement | null;
  private readonly overlay: HTMLElement;
  private readonly openClass: string;
  private readonly mobileClass: string;
  private readonly activeOverlayClass: string;
  private readonly mobileBreakpoint: number;
  private readonly closeButtons: NodeListOf<HTMLElement>;

  constructor(private readonly element: HTMLElement) {
    this.button = element.querySelector<HTMLElement>(
      element.dataset.filtersButtonSelector || "[data-filters-button]",
    );

    this.panel = element.querySelector<HTMLElement>(
      element.dataset.filtersPanelSelector || "[data-filters-panel]",
    );

    this.closeButtons = element.querySelectorAll<HTMLElement>(
      element.dataset.filtersCloseButtonSelector ||
        "[data-filters-close-button]",
    );

    this.openClass = element.dataset.filtersOpenClass || "is-open";
    this.mobileClass = element.dataset.filtersMobileClass || "is-mobile";
    this.activeOverlayClass =
      element.dataset.filtersOverlayClass || "is-active";

    this.mobileBreakpoint = Number(element.dataset.filtersBreakpoint || 768);

    // overlay dinámico
    this.overlay = document.createElement("div");
    this.overlay.className = "trv-filters-overlay";
    document.body.appendChild(this.overlay);
  }

  init(): void {
    if (!this.button || !this.panel) {
      console.warn("Filters plugin: Missing button or panel", this.element);
      return;
    }

    this.initializeState();
    this.bindEvents();
  }

  private initializeState(): void {
    this.panel!.style.display = "none";
    this.panel!.setAttribute("aria-hidden", "true");
    this.button?.setAttribute("aria-expanded", "false");
  }

  private bindEvents(): void {
    this.button?.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggle();
    });

    this.overlay.addEventListener("click", () => this.close());

    this.closeButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    });

    document.addEventListener("click", (e) => {
      if (!this.isOpen()) return;

      const target = e.target as Node;

      if (
        this.panel &&
        this.button &&
        !this.panel.contains(target) &&
        !this.button.contains(target)
      ) {
        this.close();
      }
    });

    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        this.close();
      }
    });

    window.addEventListener("resize", () => {
      if (!this.isMobile()) {
        this.panel?.classList.remove(this.mobileClass);
        document.body.style.overflow = "";
      }
      if (this.isOpen()) this.close();
    });
  }

  private toggle(): void {
    this.isOpen() ? this.close() : this.open();
  }

  private open(): void {
    if (!this.panel || !this.button) return;

    this.panel.style.display = "block";
    this.panel.setAttribute("aria-hidden", "false");
    this.button.setAttribute("aria-expanded", "true");

    this.element.classList.add(this.openClass);

    if (this.isMobile()) {
      this.panel.classList.add(this.mobileClass);
      this.overlay.classList.add(this.activeOverlayClass);
      document.body.style.overflow = "hidden";
    }

    this.dispatchEvent("filtersOpened");
  }

  private close(): void {
    if (!this.panel || !this.button) return;

    this.panel.style.display = "none";
    this.panel.setAttribute("aria-hidden", "true");
    this.button.setAttribute("aria-expanded", "false");

    this.element.classList.remove(this.openClass);
    this.panel.classList.remove(this.mobileClass);
    this.overlay.classList.remove(this.activeOverlayClass);

    document.body.style.overflow = "";

    this.dispatchEvent("filtersClosed");
  }

  private isOpen(): boolean {
    return this.element.classList.contains(this.openClass);
  }

  private isMobile(): boolean {
    return window.innerWidth < this.mobileBreakpoint;
  }

  private dispatchEvent(name: string): void {
    this.element.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          isOpen: this.isOpen(),
          isMobile: this.isMobile(),
        },
      }),
    );
  }
}

PluginRegistry.register("trv-filters", Filters);
